import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Divider,
  Card,
  Grid,
  Chip,
  useTheme,
  Avatar,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { storeKnowledgeBaseRedux } from '../../../store/knowledge-bases';
import { useResponseHandler } from '../../../hooks/useResponseHandler';
import { StatusChip } from '../../../commons/generics/StatusChip';
import { formatDateToYmd } from '../../../commons/utils';
import { getKnowledgeBaseById } from 'store/knowledge-bases/thunks';
import { ModalViewPdfGlobal } from '../../../components/modals/viwerPdf/ModalViewPdfGlobal';
import { Visibility, InfoOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { InfoModal } from '../../../components/generics/InfoModal';

// Icons
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 20,
  padding: 32,
  borderRadius: 24,
  background: theme.palette.background.paper,
  boxShadow: `0px 24px 48px -12px ${alpha(theme.palette.text.primary, 0.1)}`,
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
}));

const InfoItem = ({
  icon,
  label,
  value,
  onInfoClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  onInfoClick?: () => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      p: 2.5,
      borderRadius: 4,
      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),
      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        bgcolor: 'background.paper',
        transform: 'translateY(-4px)',
        boxShadow: (theme) => `0 12px 24px -8px ${alpha(theme.palette.text.primary, 0.1)}`,
      },
    }}
  >
    <Avatar
      sx={{
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        color: 'primary.main',
        width: 48,
        height: 48,
        borderRadius: 3,
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography
          variant="overline"
          color="textSecondary"
          sx={{ fontWeight: 700, letterSpacing: '0.5px', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        {onInfoClick && (
          <IconButton color="info" size="small" onClick={onInfoClick} sx={{ p: 0 }}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box
        sx={{
          typography: 'body1',
          fontWeight: 600,
          color: 'text.primary',
          wordBreak: 'break-word',
          mt: 0.5,
        }}
      >
        {value}
      </Box>
    </Box>
  </Box>
);

const SectionTitle = ({ title, icon }: { title: string; icon: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
      }}
    >
      {icon}
    </Box>
    <Typography variant="h5" color="textPrimary" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
      {title}
    </Typography>
  </Box>
);

export const DetailBaseConocimiento = () => {
  const { id } = useParams();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { knowledge_base } = useSelector((state: any) => state.knowledgeBases);
  const { handleResponse, ResponseComponents } = useResponseHandler() as any;

  // Estado global del modal PDF (igual que en DetailEmployee)
  const [showPdfDetail, setShowPdfDetail] = React.useState(false);
  const [urlRutPdf, setUrlRutPdf] = React.useState<string | null>(null);
  const [typeUrl, setTypeUrl] = React.useState<string | null>(null);
  const [activeModal, setActiveModal] = React.useState<string | null>(null);

  const getModalContent = () => {
    switch (activeModal) {
      case 'name':
        return {
          title: 'Nombre',
          description: 'El nombre identificador de esta base de conocimiento.',
          infoText: 'Ejemplo: "Reglamento del Hotel" o "Políticas de Cancelación".',
        };
      case 'description':
        return {
          title: 'Descripción',
          description:
            'Una breve explicación del contenido o propósito de esta base de conocimiento.',
          infoText: 'Ejemplo: "Contiene las normas internas y horarios para los empleados."',
        };
      case 'embeddingProvider':
        return {
          title: 'Proveedor de Embedding',
          description:
            'La empresa que provee el modelo matemático que convertirá tus textos en vectores.',
          infoText:
            'Ejemplo: Google (Gemini) u OpenAI. Esto define qué infraestructura procesará tus documentos.',
        };
      case 'embeddingModel':
        return {
          title: 'Modelo de Embedding',
          description:
            'El modelo de inteligencia artificial específico utilizado para procesar los textos.',
          infoText:
            'Ejemplo: gemini-1.5-pro o text-embedding-3-small. Diferentes modelos tienen diferentes niveles de comprensión y costos.',
        };
      default:
        return { title: '', description: '', infoText: '' };
    }
  };

  const modalContent = getModalContent();

  const handleOpenPdf = (url: string, urlTypeUrl: string) => {
    setUrlRutPdf(url);
    setTypeUrl(urlTypeUrl);
    setShowPdfDetail(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        const response = await dispatch(getKnowledgeBaseById(id));
        if (!response?.ok) {
          navigate('/inteligencia-artificial/base-conocimiento');
        }
      }
    };
    fetchData();

    return () => {
      dispatch(storeKnowledgeBaseRedux(null));
    };
  }, [dispatch, id, navigate]);

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          fontSize={28}
          color="textPrimary"
          sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}
        >
          Detalles de Base de Conocimiento
        </Typography>
        <Breadcrumbs aria-label="breadcrumb">
          <Typography color="textSecondary" sx={{ fontWeight: 600 }}>
            IA
          </Typography>
          <Link to="/inteligencia-artificial/base-conocimiento">Bases de Conocimiento</Link>
          <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
            Detalle
          </Typography>
        </Breadcrumbs>
      </Box>

      <StyledCard>
        <Box sx={{ mb: 6 }}>
          <SectionTitle
            title="Información General"
            icon={<DescriptionOutlinedIcon fontSize="small" />}
          />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<TitleOutlinedIcon />}
                label="Nombre"
                value={knowledge_base?.name || 'N/A'}
                onInfoClick={() => setActiveModal('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<AutoAwesomeOutlinedIcon />}
                label="Descripción"
                value={knowledge_base?.description || 'N/A'}
                onInfoClick={() => setActiveModal('description')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<CalendarMonthOutlinedIcon />}
                label="Fecha de Creación"
                value={
                  knowledge_base?.createdAt ? formatDateToYmd(knowledge_base.createdAt) : 'N/A'
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<LayersOutlinedIcon />}
                label="Estado"
                value={<StatusChip status={knowledge_base?.status ? 1 : 0} />}
              />
            </Grid>
            {knowledge_base?.originalName && (
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <InfoItem
                  icon={<DescriptionOutlinedIcon />}
                  label="Archivo Original"
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {knowledge_base.originalName}
                      </Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenPdf(knowledge_base.fileName, 'knowledge-bases')}
                        title="Ver documento"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                />
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 5, borderStyle: 'dashed', borderColor: 'rgba(145, 158, 171, 0.24)' }} />

        <Box sx={{ mb: 2 }}>
          <SectionTitle title="Datos Técnicos" icon={<MemoryOutlinedIcon fontSize="small" />} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<DataObjectOutlinedIcon />}
                label="Modelo de Embedding"
                value={knowledge_base?.embeddingModel || 'N/A'}
                onInfoClick={() => setActiveModal('embeddingModel')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<StorageOutlinedIcon />}
                label="Proveedor"
                value={
                  <Chip
                    label={knowledge_base?.embeddingProvider || 'N/A'}
                    size="small"
                    color="default"
                    sx={{ fontWeight: 600, borderRadius: 2 }}
                  />
                }
                onInfoClick={() => setActiveModal('embeddingProvider')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<LayersOutlinedIcon />}
                label="Vectores Generados"
                value={
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {knowledge_base?.vectorCount?.toLocaleString() || 0}
                  </Typography>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<DescriptionOutlinedIcon />}
                label="Fragmentos (Chunks)"
                value={
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {knowledge_base?.chunks?.toLocaleString() || 0}
                  </Typography>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<AutoAwesomeOutlinedIcon />}
                label="Procesamiento"
                value={
                  <Chip
                    label={
                      knowledge_base?.embeddingStatus === 'COMPLETED'
                        ? 'Procesado'
                        : knowledge_base?.embeddingStatus === 'PENDING'
                          ? 'En proceso'
                          : knowledge_base?.embeddingStatus || 'Pendiente'
                    }
                    color={knowledge_base?.embeddingStatus === 'COMPLETED' ? 'success' : 'warning'}
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Box>
      </StyledCard>

      <ModalViewPdfGlobal
        open={showPdfDetail}
        typeUrl={typeUrl}
        onClose={() => setShowPdfDetail(false)}
        pdfUrl={urlRutPdf}
      />

      <InfoModal
        open={!!activeModal}
        setOpen={(isOpen: boolean) => {
          if (!isOpen) setActiveModal(null);
        }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </Box>
  );
};
